import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import {
  Plus, Search, Trash2, BookOpen, Loader2, ChevronLeft, ChevronRight,
  Edit, ToggleLeft, ToggleRight, Image, FileText, Layers,
  CheckCircle, XCircle, Clock, Send, AlertTriangle
} from 'lucide-react';

const SUBJECTS = ['Mathematics', 'Physics', 'Chemistry'];
const DIFFICULTIES = ['Easy', 'Medium', 'Hard'];
const STATUSES = ['draft', 'pending', 'approved', 'rejected'];

const subjectColors = {
  Mathematics: 'text-blue-700 bg-blue-50 border border-blue-200',
  Physics: 'text-green-700 bg-green-50 border border-green-200',
  Chemistry: 'text-amber-700 bg-amber-50 border border-amber-200'
};
const difficultyColors = {
  Easy: 'text-green-700 bg-green-100',
  Medium: 'text-amber-700 bg-amber-100',
  Hard: 'text-red-700 bg-red-100'
};
const statusConfig = {
  draft:    { label: 'Draft',    color: '#6b7280', bg: '#f3f4f6', icon: <FileText size={12} /> },
  pending:  { label: 'Pending',  color: '#f59e0b', bg: '#fef3c7', icon: <Clock size={12} /> },
  approved: { label: 'Approved', color: '#16a34a', bg: '#dcfce7', icon: <CheckCircle size={12} /> },
  rejected: { label: 'Rejected', color: '#dc2626', bg: '#fee2e2', icon: <XCircle size={12} /> },
};

function StatusBadge({ status }) {
  const cfg = statusConfig[status] || statusConfig.draft;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '600',
      color: cfg.color, background: cfg.bg
    }}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

function RejectModal({ onConfirm, onCancel }) {
  const [reason, setReason] = useState('');
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: '16px', padding: '28px', width: '400px', maxWidth: '90vw' }}>
        <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '700' }}>Reject Question</h3>
        <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 16px' }}>Provide a reason for rejection (optional)</p>
        <textarea
          value={reason} onChange={e => setReason(e.target.value)}
          placeholder="e.g. Question is ambiguous..."
          style={{ width: '100%', boxSizing: 'border-box', padding: '10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', minHeight: '80px', resize: 'vertical' }}
        />
        <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
          <button onClick={onCancel} style={{ flex: 1, padding: '10px', borderRadius: '10px', background: '#f3f4f6', border: 'none', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
          <button onClick={() => onConfirm(reason)} style={{ flex: 1, padding: '10px', borderRadius: '10px', background: '#dc2626', border: 'none', cursor: 'pointer', color: 'white', fontWeight: '600' }}>Reject</button>
        </div>
      </div>
    </div>
  );
}

export default function QuestionsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSuperAdmin, canApproveQuestions, canEditQuestions, canDeleteQuestions, canAddQuestions } = useAuth();

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('');
  const [filterStatus, setFilterStatus] = useState(() => {
    const p = new URLSearchParams(location.search);
    return p.get('status') || '';
  });
  const [showAll, setShowAll] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [uploadLoading, setUploadLoading] = useState(false);
  const [stats, setStats] = useState({});
  const [togglingId, setTogglingId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);

  const superadmin = isSuperAdmin();
  const canApprove = canApproveQuestions();

  useEffect(() => { fetchQuestions(); }, [search, filterSubject, filterDifficulty, filterStatus, page, showAll]);
  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      const { data } = await api.get('/questions/stats');
      const bySubject = {};
      data.data.bySubject.forEach(s => { bySubject[s._id] = s.count; });
      const byStatus = {};
      data.data.byStatus.forEach(s => { byStatus[s._id] = s.count; });
      setStats({ bySubject, byStatus, total: data.data.total });
    } catch {}
  };

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.append('search', search);
      if (filterSubject) params.append('subject', filterSubject);
      if (filterDifficulty) params.append('difficulty', filterDifficulty);
      if (filterStatus) params.append('status', filterStatus);
      if (showAll) params.append('showAll', 'true');
      const { data } = await api.get(`/questions?${params}`);
      setQuestions(data.data);
      setPagination(data.pagination);
    } catch { toast.error('Failed to load questions'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await api.delete(`/questions/${id}`);
      toast.success('Deleted');
      fetchQuestions(); fetchStats();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to delete'); }
  };

  const handleToggleActive = async (id, current) => {
    if (togglingId) return;
    setTogglingId(id);
    setQuestions(prev => prev.map(q => q._id === id ? { ...q, isActive: !current } : q));
    try {
      const { data } = await api.patch(`/questions/${id}/toggle-active`);
      setQuestions(prev => prev.map(q => q._id === id ? { ...q, isActive: data.data.isActive } : q));
      toast.success(data.data.isActive ? '✅ Activated' : '🔴 Deactivated');
    } catch (err) {
      setQuestions(prev => prev.map(q => q._id === id ? { ...q, isActive: current } : q));
      toast.error(err.response?.data?.message || 'Failed');
    } finally { setTogglingId(null); }
  };

  const handleSubmitForApproval = async (id) => {
    setActionLoading(id + '_submit');
    try {
      await api.patch(`/questions/${id}/submit`);
      toast.success('Submitted for approval');
      fetchQuestions(); fetchStats();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setActionLoading(null); }
  };

  const handleApprove = async (id) => {
    setActionLoading(id + '_approve');
    try {
      await api.patch(`/questions/${id}/review`, { action: 'approve' });
      toast.success('✅ Question approved!');
      fetchQuestions(); fetchStats();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setActionLoading(null); }
  };

  const handleReject = async (id, reason) => {
    setRejectTarget(null);
    setActionLoading(id + '_reject');
    try {
      await api.patch(`/questions/${id}/review`, { action: 'reject', rejectionReason: reason });
      toast.success('Question rejected');
      fetchQuestions(); fetchStats();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setActionLoading(null); }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploadLoading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await api.post('/questions/bulk-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(data.message); fetchQuestions(); fetchStats();
    } catch (err) { toast.error(err.response?.data?.message || 'Upload failed'); }
    finally { setUploadLoading(false); e.target.value = ''; }
  };

  const TYPE_ICONS = {
    text: <FileText size={13} />,
    image: <Image size={13} />,
    hybrid: <Layers size={13} />
  };

  const pendingCount = stats.byStatus?.pending || 0;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Question Bank</h1>
          <p className="text-gray-500 mt-1">
            {stats.total || 0} approved questions
            {pendingCount > 0 && (superadmin || canApprove) && (
              <span style={{ marginLeft: '10px', color: '#f59e0b', fontWeight: '600' }}>
                · {pendingCount} pending review
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* All admins can add/upload questions — backend enforces auth */}
          {(
            <>
              <label className="btn-secondary flex items-center gap-2 text-sm cursor-pointer">
                {uploadLoading ? <Loader2 size={16} className="animate-spin" /> : null}
                Bulk Upload
                <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleBulkUpload} />
              </label>
              <button onClick={() => navigate('/admin/questions/add')} className="btn-primary flex items-center gap-2 text-sm">
                <Plus size={16} /> Add Question
              </button>
            </>
          )}
        </div>
      </div>

      {/* Pending approval alert */}
      {(superadmin || canApprove) && pendingCount > 0 && !filterStatus && (
        <div style={{
          background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '12px',
          padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <AlertTriangle size={18} color="#f59e0b" />
            <span style={{ color: '#92400e', fontWeight: '600', fontSize: '14px' }}>
              {pendingCount} question{pendingCount !== 1 ? 's' : ''} pending your review
            </span>
          </div>
          <button onClick={() => { setFilterStatus('pending'); setPage(1); }}
            style={{ background: '#f59e0b', color: 'white', border: 'none', padding: '6px 14px', borderRadius: '8px', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}>
            Review Now
          </button>
        </div>
      )}

      {/* Subject stats */}
      <div className="grid grid-cols-3 gap-4">
        {SUBJECTS.map(s => (
          <div key={s} className={`card cursor-pointer transition-all ${filterSubject === s ? 'ring-2 ring-gray-800' : ''}`}
            onClick={() => { setFilterSubject(filterSubject === s ? '' : s); setPage(1); }}>
            <p className="text-sm text-gray-500">{s}</p>
            <p className="text-2xl font-bold text-gray-900">{stats.bySubject?.[s] || 0}</p>
            <p className="text-xs text-gray-400">approved</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input type="text" placeholder="Search by question or code..." className="input-field pl-10"
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <select className="input-field w-auto" value={filterSubject} onChange={e => { setFilterSubject(e.target.value); setPage(1); }}>
          <option value="">All Subjects</option>
          {SUBJECTS.map(s => <option key={s}>{s}</option>)}
        </select>
        <select className="input-field w-auto" value={filterDifficulty} onChange={e => { setFilterDifficulty(e.target.value); setPage(1); }}>
          <option value="">All Difficulties</option>
          {DIFFICULTIES.map(d => <option key={d}>{d}</option>)}
        </select>
        <select className="input-field w-auto" value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}>
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s} style={{ textTransform: 'capitalize' }}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
          <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} className="rounded" /> Show inactive
        </label>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                {['Code', 'Type', 'Question', 'Subject', 'Diff.', 'Status', 'Created By', 'Actions'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-gray-500 font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-16"><div className="loader mx-auto" /></td></tr>
              ) : questions.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-16 text-gray-400">
                  <BookOpen size={40} className="mx-auto mb-2 opacity-30" /><p>No questions found</p>
                </td></tr>
              ) : questions.map(q => {
                const isApprovedQ = q.status === 'approved';
                const canEditQ = true; // all admins can edit (UI consistent for everyone)
                const canDeleteQ = superadmin; // delete restricted to superadmin only

                return (
                  <tr key={q._id} className={`border-b border-gray-50 hover:bg-gray-50 transition-colors ${!q.isActive ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded font-bold">{q.questionCode}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-1 text-xs text-gray-500 capitalize">
                        {TYPE_ICONS[q.questionType || 'text']} {q.questionType || 'text'}
                      </span>
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      {q.questionImage && <img src={q.questionImage} alt="Q" className="h-10 rounded border mb-1 object-contain" />}
                      {q.questionText && <p className="text-gray-800 line-clamp-1">{q.questionText}</p>}
                      {q.status === 'rejected' && q.rejectionReason && (
                        <p className="text-xs text-red-500 mt-1">❌ {q.rejectionReason}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${subjectColors[q.subject]}`}>{q.subject}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${difficultyColors[q.difficulty]}`}>{q.difficulty}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <StatusBadge status={q.status} />
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {q.createdBy?.name || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 flex-wrap">
                        {/* Submit for approval — admin's own draft/rejected questions */}
                        {!superadmin && ['draft', 'rejected'].includes(q.status) && canAddQuestions() && (
                          <button
                            onClick={() => handleSubmitForApproval(q._id)}
                            disabled={actionLoading === q._id + '_submit'}
                            title="Submit for approval"
                            className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                          >
                            {actionLoading === q._id + '_submit' ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                          </button>
                        )}

                        {/* Approve — superadmin / approve_questions on pending */}
                        {(superadmin || canApprove) && q.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApprove(q._id)}
                              disabled={actionLoading === q._id + '_approve'}
                              title="Approve"
                              style={{ padding: '6px', background: '#f0fdf4', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex' }}
                            >
                              {actionLoading === q._id + '_approve' ? <Loader2 size={15} color="#16a34a" className="animate-spin" /> : <CheckCircle size={15} color="#16a34a" />}
                            </button>
                            <button
                              onClick={() => setRejectTarget(q._id)}
                              title="Reject"
                              style={{ padding: '6px', background: '#fef2f2', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex' }}
                            >
                              <XCircle size={15} color="#dc2626" />
                            </button>
                          </>
                        )}

                        {/* Toggle active */}
                        <button
                          onClick={() => handleToggleActive(q._id, q.isActive)}
                          disabled={togglingId === q._id}
                          title={q.isActive ? 'Deactivate' : 'Activate'}
                          className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold border transition-all ${
                            q.isActive
                              ? 'text-green-700 bg-green-50 border-green-200'
                              : 'text-gray-500 bg-gray-50 border-gray-200'
                          } disabled:opacity-50`}
                        >
                          {togglingId === q._id ? <Loader2 size={13} className="animate-spin" /> : q.isActive ? <ToggleRight size={15} /> : <ToggleLeft size={15} />}
                          {q.isActive ? 'On' : 'Off'}
                        </button>

                        {/* Edit */}
                        {canEditQ && (
                          <button onClick={() => navigate(`/admin/questions/edit/${q._id}`)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit">
                            <Edit size={15} />
                          </button>
                        )}

                        {/* Delete */}
                        {canDeleteQ && (
                          <button onClick={() => handleDelete(q._id)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg" title="Delete">
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
            <p className="text-sm text-gray-500">Page {page} of {pagination.pages} ({pagination.total} total)</p>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="btn-secondary py-1.5 px-3 disabled:opacity-40"><ChevronLeft size={16} /></button>
              <button disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)} className="btn-secondary py-1.5 px-3 disabled:opacity-40"><ChevronRight size={16} /></button>
            </div>
          </div>
        )}
      </div>

      {/* Reject modal */}
      {rejectTarget && (
        <RejectModal
          onConfirm={(reason) => handleReject(rejectTarget, reason)}
          onCancel={() => setRejectTarget(null)}
        />
      )}
    </div>
  );
}
